import type { Question } from '@exam-management/common/qcontent-provider';
import { Link } from '@exam-management/router';
import {
  Box,
  Button,
  Chip,
  Collapse,
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Paper,
  ReadonlyField,
  Rotate,
  styled,
  Text,
  VisibilityOutlinedIcon,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';

import { QuestionChoiceList } from '../question-choice-list';
import { QuestionContentPreviewer } from '../question-content-previewer';

const Wrapper = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.rounded(4),
  boxShadow: 'none',
  width: '100%',
}));

const QuestionPreviewSummary = styled(Flex)({
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',
});

const QuestionWrapper = styled(Flex)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const QuestionPreviewSummaryHeader = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  cursor: 'pointer',
  gap: theme.spacing(4),
  padding: theme.spacing(3, 4),
  width: '100%',
}));

interface QuestionPreviewCollapsibleProps {
  question: Pick<
    Question,
    | 'questionGroup'
    | 'id'
    | 'refQBankStructureId'
    | 'refCode'
    | 'correctAnswer'
    | 'details'
    | 'groupOrder'
  > & {
    metadata: Pick<Question['metadata'], 'choices'>;
  };
  endAdornment?: JSX.Element;
}

export default function QuestionPreviewCollapsible(
  props: Readonly<QuestionPreviewCollapsibleProps>,
) {
  const { question, endAdornment } = props;
  const [isCollapseOpen, collapseOpenHandlers] = useDisclosure();
  const { toggle: toggleCollapseOpen } = collapseOpenHandlers;
  const {
    correctAnswer,
    details,
    id,
    groupOrder,
    metadata,
    questionGroup,
    refCode,
    refQBankStructureId,
  } = question;

  return (
    <Wrapper>
      <QuestionPreviewSummary>
        <QuestionPreviewSummaryHeader onClick={toggleCollapseOpen}>
          <Text variant='h4'>ข้อที่ {groupOrder}</Text>
          <Box sx={{ flexGrow: 1 }}>
            <Chip
              label={refCode}
              sx={{
                fontWeight: (theme) => theme.typography.body2Semibold.fontWeight,
              }}
            />
          </Box>

          <Button
            component={Link}
            to='/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId'
            params={{
              structureId: refQBankStructureId,
              questionId: questionGroup?.id ?? '',
              subQuestionId: id,
            }}
            variant='outlined'
            color='secondary'
            startIcon={<VisibilityOutlinedIcon />}
            size='small'
            sx={{ minWidth: 'unset' }}
          >
            View
          </Button>
          <Box>{endAdornment}</Box>

          <IconButton>
            <Rotate in={isCollapseOpen}>
              <ExpandMoreRoundedIcon />
            </Rotate>
          </IconButton>
        </QuestionPreviewSummaryHeader>
        <QuestionWrapper>
          <ReadonlyField
            content={<QuestionContentPreviewer content={details ?? ''} questionId={id} />}
          />
        </QuestionWrapper>
      </QuestionPreviewSummary>
      <Collapse in={isCollapseOpen} timeout='auto'>
        <QuestionChoiceList
          choices={metadata?.choices ?? []}
          questionId={id}
          value={correctAnswer?.value ?? ''}
          sx={(theme) => ({ p: theme.spacing(0, 4, 4, 4) })}
        />
      </Collapse>
    </Wrapper>
  );
}
